import { SaveOutlined } from '@ant-design/icons';
import { Col, Form, Input, InputNumber, message, Modal, Row, Space } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { RegionModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import CreateBtn from '@/components/app/CreateBtn';
import { formatCurrency } from '@/helpers/format';
import StationInput from '@/components/FormItems/StationInput';
import BoxCategoryInput from '@/components/FormItems/shipment/BoxCategoryInput';
import BoxWeightInput from '@/components/FormItems/shipment/BoxWeightInput';
import BoxVolumeInput from '@/components/FormItems/shipment/BoxVolumeInput';

const columns: IColumnType<RegionModel>[] = [
  {
    title: 'base.Source',
    dataIndex: 'sourceName',
  },
  {
    title: 'base.Destination',
    dataIndex: 'destinationName',
  },
  {
    title: 'shipmentManagementPage_BoxCategory',
    dataIndex: 'boxCategoryName',
  },
  {
    title: 'shipmentManagementPage_BoxWeight',
    dataIndex: 'boxWeightName',
  },
  {
    title: 'shipmentManagementPage_BoxVolume',
    dataIndex: 'boxVolumeName',
  },
  {
    title: 'shipmentManagementPage_TariffAmount',
    dataIndex: 'tariffAmount',
    render: v => <b>{formatCurrency(v)}</b>,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'SELECT', name: 'SourceId', label: 'base.Source', customElm: <StationInput /> },
  { type: 'SELECT', name: 'DestinationId', label: 'base.Destination', customElm: <StationInput /> },
  // { type: 'SELECT', name: 'BoxCategoryId', label: 'shipmentManagementPage_BoxCategory', customElm: <BoxCategoryInput /> },
  // { type: 'SELECT', name: 'BoxWeightId', label: 'shipmentManagementPage_BoxWeight', customElm: <BoxWeightInput /> },
  // { type: 'SELECT', name: 'BoxVolumeId', label: 'shipmentManagementPage_BoxVolume', customElm: <BoxVolumeInput /> },
];

const ShipmentTariffTab = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const createData = values => {
    setLoading(true);
    apiSdk.ShipmentTariffService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateData = values => {
    setLoading(true);
    apiSdk.ShipmentTariffService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/shipments/shipment-tariffs/get-shipment-tariffs"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/shipments/shipment-tariffs/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <Space>
            <CreateBtn
              onClick={() => {
                setIsNewForm(true);
                setFormVisible(true);
                form.resetFields();
              }}
            />
          </Space>
        }
      />
      <Modal
        visible={formVisible}
        maskClosable={false}
        width={ModalWidth.small}
        className="nta-custom-form"
        title={isNewForm ? t('shipmentManagementPage_CreateTariffAmount') : t('shipmentManagementPage_UpdateTariffAmount')}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        onOk={() => {
          form
            .validateFields()
            .then(values => {
              if (values.id) {
                updateData(values);
              } else {
                createData(values);
              }
            })
            .catch(info => {});
        }}
      >
        <Form form={form} size="middle" layout="vertical">
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={6}>
            <Col md={24}>
              <Form.Item label={t('base.Source')} name="sourceId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <StationInput />
              </Form.Item>
              <Form.Item label={t('base.Destination')} name="destinationId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <StationInput />
              </Form.Item>
              <Form.Item
                label={t('shipmentManagementPage_BoxCategory')}
                name="boxCategoryId"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <BoxCategoryInput />
              </Form.Item>
              <Form.Item label={t('shipmentManagementPage_BoxWeight')} name="boxWeightId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <BoxWeightInput />
              </Form.Item>
              <Form.Item label={t('shipmentManagementPage_BoxVolume')} name="boxVolumeId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <BoxVolumeInput />
              </Form.Item>
              <Form.Item
                label={t('shipmentManagementPage_TariffAmount')}
                name="tariffAmount"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};
export default ShipmentTariffTab;
